/**
 * Sheets are drawn from `sheetIds.difference(hidden)`, not from the isolation keep-set.
 * A visible sheet is absent from that keep-set, so isolation must not treat that absence as hidden.
 */
export function selectionIdIsHidden(
    id: number,
    hiddenIds: { has(id: number): boolean },
    isolationVisibleIds: { has(id: number): boolean } | undefined,
    pdfObjectIds: { has(id: number): boolean } | undefined,
): boolean {
    if (pdfObjectIds?.has(id)) {
        return hiddenIds.has(id);
    }
    return isolationVisibleIds ? !isolationVisibleIds.has(id) : hiddenIds.has(id);
}

export function partitionModelAndSheetIds(
    ids: ArrayLike<number>,
    pdfObjectIds: { has(id: number): boolean; size: number } | undefined,
): { modelIds: number[]; sheetIds: number[] } {
    const modelIds: number[] = [];
    const sheetIds: number[] = [];
    if (!pdfObjectIds || pdfObjectIds.size === 0) {
        for (let index = 0; index < ids.length; index++) {
            modelIds.push(ids[index]);
        }
        return { modelIds, sheetIds };
    }
    for (let index = 0; index < ids.length; index++) {
        const id = ids[index];
        if (pdfObjectIds.has(id)) {
            sheetIds.push(id);
        } else {
            modelIds.push(id);
        }
    }
    return { modelIds, sheetIds };
}

/**
 * Model ids stay on the isolation keep-set. Sheet ids go through the hidden set,
 * which is what actually removes them from the sheet highlight group.
 */
export function applyIsolationSelectionVisibility(
    ids: ArrayLike<number>,
    pdfObjectIds: { has(id: number): boolean; size: number } | undefined,
    visible: boolean,
    setIdsVisible: (ids: number[], visible: boolean) => void,
    hideSheets: (ids: number[]) => void,
    showSheets: (ids: number[]) => void,
): void {
    const { modelIds, sheetIds } = partitionModelAndSheetIds(ids, pdfObjectIds);
    if (modelIds.length > 0) {
        setIdsVisible(modelIds, visible);
    }
    if (sheetIds.length === 0) {
        return;
    }
    if (visible) {
        showSheets(sheetIds);
    } else {
        hideSheets(sheetIds);
    }
}
